'use client';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { jsPDF } from 'jspdf';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AnnouncementDateSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/announcement-date-section/announcement-date-section';
import { ContactInformationSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/contact-information-section/contact-information-section';
import { PdfPreview } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/pdf-preview/pdf-preview';
import { PersonalAnecdoteSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/personal-anecdote-section/personal-anecdote-section';
import { PersonalInformationSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/personal-information-section/personal-information-section';
import { PhotoSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/photo-section/photo-section';
import { ProfessionalInformationSection } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/components/professional-information-section/professional-information-section';
import { type PressReleasePDFContent } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/get-press-release-pdf-content';
import { usePressReleaseLogos } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/utils/pdf-header-images-helpers/use-press-release-logos';
import { usePdfFontLoader } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/use-pdf-font-loader';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { usePathname, useRouter } from '@module/mdrt-org/shared/i18n/i18n-navigation';
import { HOMEPAGE_PATH } from '@module/mdrt-org/shared/utils/constants/local-paths';
import { PRESS_RELEASE } from '@module/mdrt-org/shared/utils/constants/pdf-modals-search-constants';
import { PressReleasePdfType } from '@module/mdrt-org/shared/utils/enums/press-release-pdf-type';
import { buildPhoneIsoCodeOptions } from '@module/mdrt-org/shared/utils/helpers/build-phone-iso-code-options';
import { getDefaultPhoneIsoCodeOption } from '@module/mdrt-org/shared/utils/helpers/get-default-phone-iso-code-option';
import { type Country } from '@module/mdrt-org/shared/utils/types/countries';
import { type CustomerProfile } from '@module/mdrt-org/shared/utils/types/customer-profile';
import { Alert } from '@shared/ui-components/alert/alert';
import { AlertVariant } from '@shared/ui-components/alert/alertVariant';
import { DialogActions } from '@shared/ui-components/dialog/components/dialog-actions/dialog-actions';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { ButtonType } from '@shared/utils/enums/button-type';
import { getLocalDate } from '@shared/utils/helpers/get-local-date';
import { formatSocialLink, SocialPlatform } from '@shared/utils/helpers/social-link-validators';
import { useCssVariable } from '@shared/utils/hooks/use-css-variable';
import { type SelectOption } from '@shared/utils/types/select-option';
import { DonationSection } from './components/donation-section/donation-section';
import { PdfDownloadModalContent } from './components/pdf-download-modal-content/pdf-download-modal-content';
import photoPlaceholder from './components/photo-section/photo-placeholder.png';
import { type PressReleaseForm } from './utils/press-release-form-types';
import styles from './press-release-pdf-modal.module.scss';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { GTM_ACTION_CLICK, GTM_EVENT_NAME_INTERNAL_LINK_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
export type PressReleasePdfModalProps = {
  cmsContent: PressReleasePDFContent;
  countriesData: Country[];
  customerProfile: CustomerProfile;
  designations: SelectOption[];
  initialStateProvince: string | undefined;
  profilePictureSource: Nullable<string>;
};
export const PressReleasePdfModal = ({
  cmsContent,
  countriesData,
  customerProfile,
  designations,
  initialStateProvince,
  profilePictureSource
  // eslint-disable-next-line complexity
}: PressReleasePdfModalProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParameters = useSearchParams();
  const pdfContentRef = useRef<HTMLDivElement | null>(null);
  const modalType = searchParameters.get(PRESS_RELEASE);
  const phoneIsoCodeOptions = buildPhoneIsoCodeOptions(countriesData);
  const initialIsoCode = getDefaultPhoneIsoCodeOption({
    countriesData,
    countryId: customerProfile?.countryId,
    phoneIsoCodeOptions
  });
  const [PDF_FONT_FAMILY] = useCssVariable('--pdf-font-family');
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);
  const [activeField, setActiveField] = useState<null | string>(null);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const initialDate = getLocalDate();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [profilePictureOverride, setProfilePictureOverride] = useState<Nullable<string> | undefined>();
  const savedProfilePictureSource = profilePictureOverride === undefined ? profilePictureSource : profilePictureOverride;
  const pdfMap = {
    [PressReleasePdfType.COTAchievement]: cmsContent.cotMemberAchievementPressRelease,
    [PressReleasePdfType.LifeAchievement]: cmsContent.lifeMemberAchievementPressRelease,
    [PressReleasePdfType.MDRTAchievement]: cmsContent.mdrtMemberAchievementPressRelease,
    [PressReleasePdfType.MDRTFoundationGrant]: cmsContent.mdrtFoundationGrantPressRelease,
    [PressReleasePdfType.TOTAchievement]: cmsContent.totMemberAchievementPressRelease
  };
  const isPressReleaseModalType = modalType === PressReleasePdfType.COTAchievement || modalType === PressReleasePdfType.MDRTAchievement || modalType === PressReleasePdfType.TOTAchievement || modalType === PressReleasePdfType.LifeAchievement || modalType === PressReleasePdfType.MDRTFoundationGrant;
  let templateName: string;
  if (modalType === PressReleasePdfType.MDRTAchievement) templateName = 'MDRT Member Press Release';else if (modalType === PressReleasePdfType.COTAchievement) templateName = 'Court of the Table Member Press Release';else if (modalType === PressReleasePdfType.TOTAchievement) templateName = 'Top of the Table Member Press Release';else if (modalType === PressReleasePdfType.LifeAchievement) templateName = 'Life Member Press Release';else if (modalType === PressReleasePdfType.MDRTFoundationGrant) templateName = 'MDRT Foundation Grant Recipient Press Release';
  const selectedPdfType = isPressReleaseModalType && modalType !== null ? pdfMap[modalType] : undefined;
  const {
    boldFontBinaryString,
    isLoading: isFontLoading,
    regularFontBinaryString
  } = usePdfFontLoader();
  const formMethods = useForm<PressReleaseForm>({
    ...formBaseConfig,
    defaultValues: {
      charityName: '',
      city: customerProfile?.address?.[0]?.city ?? '',
      companyName: customerProfile?.companyName || '',
      date: initialDate,
      donationInformation: '',
      email: customerProfile?.emailAddress ?? '',
      facebook: customerProfile?.facebook || '',
      firstName: customerProfile?.firstName ?? '',
      grantAmount: '',
      instagram: customerProfile?.instagram || '',
      isoCode: initialIsoCode,
      jobTitle: customerProfile?.jobTitle || '',
      lastName: customerProfile.lastName ?? '',
      linkedIn: customerProfile?.linkedin || '',
      middleName: customerProfile?.middleName || '',
      personalCompany: customerProfile?.personalCompany || '',
      phoneAreaCode: customerProfile?.phoneNumbers?.[0]?.areaCode || '',
      phoneNumber: customerProfile?.phoneNumbers?.[0]?.phoneNumber || '',
      stateProvince: initialStateProvince || '',
      twitterX: customerProfile?.twitter || ''
    },
    mode: 'onBlur'
  });
  const {
    charityName: watchCharityName,
    city: watchCity,
    companyName: watchCompanyName,
    date: watchDate,
    donationInformation: watchDonationInformation,
    email: watchEmail,
    facebook: watchFacebook,
    firstName: watchFirstName,
    grantAmount: watchGrantAmount,
    instagram: watchInstagram,
    isoCode: watchIsoCode,
    jobTitle: watchJobTitle,
    lastName: watchLastName,
    linkedIn: watchLinkedIn,
    middleName: watchMiddleName,
    personalAnecdote: watchPersonalAnecdote,
    personalCompany: watchPersonalCompany,
    phoneAreaCode: watchAreaCode,
    phoneNumber: watchPhoneNumber,
    stateProvince: watchState,
    twitterX: watchTwitterX
  } = formMethods.watch();
  const areaCodeDisplay = watchAreaCode ? String(watchAreaCode) : '';
  const PhoneNumber = watchIsoCode?.value || areaCodeDisplay || watchPhoneNumber ? `+${watchIsoCode?.value} ${areaCodeDisplay} ${watchPhoneNumber?.trim() || `[${cmsContent.formContent.phoneNumber}]`}` : `${`[${cmsContent.formContent.isoCode}]`} ${`[${cmsContent.formContent.areaCode}]`} ${`[${cmsContent.formContent.phoneNumber}]`}`;
  const CityAndStateProvince = watchCity?.trim() && watchState?.trim() ? '{{ City }}, {{ StateProvince }}' : watchState?.trim() ? `[${cmsContent.formContent.city}], {{ StateProvince }}` : '{{ City }}';
  const ContactInformationPlaceholder = [`<div class="contact-information-wrapper">`, watchMiddleName?.trim() ? `<span>{{ FirstName }} {{ MiddleName }} {{ LastName }}</span>` : `<span>{{ FirstName }} {{ LastName }}</span>`, watchJobTitle?.trim() && (watchCompanyName?.trim() || watchPersonalCompany?.trim()) ? `<span>{{ JobTitle }}, {{ CompanyName }}</span>` : !watchJobTitle?.trim() && (watchCompanyName?.trim() || watchPersonalCompany?.trim()) ? `<span>{{ CompanyName }}</span>` : `<span>{{ JobTitle }}</span>`, `{{ PhoneNumber }}`, `{{ Email }}`, watchLinkedIn?.trim() && `<a class="press-release-pdf-link" href="${encodeURI(formatSocialLink(watchLinkedIn.trim(), SocialPlatform.LinkedIn))}" rel="noreferrer" target="_blank">
               {{ LinkedInLabel }}
              </a>`, watchFacebook?.trim() && `<a class="press-release-pdf-link" href="${encodeURI(formatSocialLink(watchFacebook.trim(), SocialPlatform.Facebook))}" rel="noreferrer" target="_blank">
                {{ FacebookLabel }}
              </a>`, watchInstagram?.trim() && `<a class="press-release-pdf-link" href="${encodeURI(formatSocialLink(watchInstagram.trim(), SocialPlatform.Instagram))}" rel="noreferrer"  target="_blank">{{ InstagramLabel }}</a>`, watchTwitterX?.trim() && `<a class="press-release-pdf-link" href="${encodeURI(formatSocialLink(watchTwitterX.trim(), SocialPlatform.Twitter))}" rel="noreferrer"  target="_blank">{{ TwitterXLabel }}</a>`, `</div>`].join('\n');
  const pdfDocumentData = useMemo(
  // eslint-disable-next-line complexity
  () => selectedPdfType ? {
    AboutMDRTSectionPlaceholder: selectedPdfType.aboutMdrtSection,
    AboutProfessionalExperience: watchPersonalAnecdote?.trim() || '',
    BusinessContactInformationPlaceholder: selectedPdfType.mdrtContactSection ? `<div class="business-contact-information-wrapper">${selectedPdfType.mdrtContactSection}</div>` : undefined,
    CharityName: watchCharityName?.trim() || `[${cmsContent.formContent.charityNameInputLabel}]`,
    City: watchCity?.trim() || `[${cmsContent.formContent.city}]`,
    CityAndStateProvince,
    CompanyName: watchPersonalCompany || watchCompanyName || '',
    ContactInformationLabelPlaceholder: selectedPdfType.contactInformationLabel,
    ContactInformationPlaceholder,
    ContentArea1Placeholder: selectedPdfType.contentAreaOne,
    ContentArea2Placeholder: selectedPdfType.contentAreaTwo,
    COTMembershipYears: customerProfile?.totalCOT ?? undefined,
    Date: watchDate ? new Date(watchDate.year, watchDate.month - 1, watchDate.day).toLocaleDateString(locale) : `[${cmsContent.formContent.announcementDateHeading}]`,
    Designation: designations && customerProfile?.designations && customerProfile.designations.map(designationId => designations.find(option => option?.value === designationId)?.label).filter(Boolean).join(', ') || `[${cmsContent.formContent.designation}]`,
    DonationInformation: watchDonationInformation?.trim() || '',
    DonationReason: watchDonationInformation?.trim() || `[${cmsContent.formContent.donationReasonDescription}]`,
    DonorLevel: customerProfile?.donorLevel ?? undefined,
    Email: watchEmail?.trim() ? `<a class="press-release-pdf-link" href="mailto:${watchEmail.trim()}">${watchEmail.trim()}</a>` : `[${cmsContent.formContent.email}]`,
    Facebook: watchFacebook?.trim(),
    FacebookLabel: cmsContent.formContent.facebookLabel,
    FirstName: watchFirstName?.trim() || '',
    GrantAmount: watchGrantAmount?.trim() ? `$${watchGrantAmount.trim()}` : `[${cmsContent.formContent.grantAmountInputLabel}]`,
    HeaderPlaceholder: selectedPdfType.headingText,
    Instagram: watchInstagram || '',
    InstagramLabel: cmsContent.formContent.instagramLabel,
    JobTitle: watchJobTitle || '',
    LastName: watchLastName?.trim() || `[${cmsContent.formContent.lastName}]`,
    LinkedIn: watchLinkedIn || '',
    LinkedInLabel: cmsContent.formContent.linkedInLabel,
    MdrtAchievements: [customerProfile?.totalMembership && selectedPdfType?.mdrtMemberLabel, customerProfile?.totalCOT && selectedPdfType?.cotMemberLabel, customerProfile?.totalTOT && selectedPdfType?.totMemberLabel, customerProfile?.donorLevel && selectedPdfType?.donorLevelLabel].filter(Boolean).join(', '),
    MdrtFoundationLogoUrlPlaceholder: cmsContent.mdrtFoundationGrantPressRelease.mdrtFoundationLogo,
    MdrtLogoUrlPlaceholder: cmsContent.mdrtFoundationGrantPressRelease.mdrtLogo,
    MembershipYears: customerProfile?.totalMembership ?? undefined,
    MiddleName: watchMiddleName?.trim() || '',
    PhoneNumber: `<a class="press-release-pdf-link" href="tel:${PhoneNumber.replaceAll(/\s+/gu, '')}">${PhoneNumber}</a>`,
    ProfilePictureUrlPlaceholder: savedProfilePictureSource || photoPlaceholder.src || '',
    StateProvince: watchState?.trim(),
    SubheaderPlaceholder: selectedPdfType.subheadingText,
    TOTMembershipYears: customerProfile?.totalTOT ?? undefined,
    TwitterX: watchTwitterX || '',
    TwitterXLabel: cmsContent.formContent.twitterXLabel
  } : {},
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [customerProfile.designations, customerProfile?.totalMembership, watchDate?.day, watchDate?.month, watchDate?.year, designations, selectedPdfType, watchAreaCode, watchCity, watchCompanyName, watchEmail, watchFirstName, watchIsoCode?.value, watchJobTitle, watchLastName, watchMiddleName, watchPersonalAnecdote, watchPersonalCompany, watchPhoneNumber, watchState, watchTwitterX, watchFacebook, watchInstagram, watchLinkedIn, profilePictureSource, savedProfilePictureSource, watchDonationInformation, watchGrantAmount, watchCharityName]);
  const {
    data: htmlTemplate,
    isLoading: isHtmlTemplateLoading
  } = useQuery({
    enabled: modalType !== undefined && selectedPdfType?.templateUrl !== undefined,
    queryFn: async () => {
      if (selectedPdfType?.templateUrl === undefined) {
        throw new Error('Url is not defined');
      }
      const response = await fetch(selectedPdfType?.templateUrl);
      if (!response.ok) throw new Error('Failed to fetch template');
      return response.text();
    },
    queryKey: [selectedPdfType?.templateUrl]
  });
  const isLoading = isHtmlTemplateLoading || isPDFGenerating || isFontLoading;
  const handleModalClose = () => {
    const params = new URLSearchParams(searchParameters.toString());
    params.delete(PRESS_RELEASE);
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false
    });
    formMethods.reset();
    setIsPDFGenerating(false);
    setActiveField(null);
    setIsFormSubmitted(false);
    setIsPreviewMode(false);
    setProfilePictureOverride(undefined);
  };
  const modalProps = {
    hasCloseButton: true,
    onModalClose: handleModalClose,
    title: selectedPdfType?.modalTitle ?? ''
  };
  const FORM_CONTAINER_CLASSNAME = 'form-container';
  const JSPDF_WINDOW_WIDTH = 800;
  const PDF_SCALE = 3.5;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pdfDocumentRef = useRef<jsPDF>(new jsPDF());
  const pdfHeaderImage = selectedPdfType?.headerImage || '';
  const pdfHeadingLogoLeft = cmsContent.mdrtFoundationGrantPressRelease.mdrtFoundationLogo || '';
  const pdfHeadingLogoRight = cmsContent.mdrtFoundationGrantPressRelease.mdrtLogo || '';
  const {
    leftLogoHeightRef,
    leftLogoWidthRef,
    rightLogoHeightRef,
    rightLogoWidthRef
  } = usePressReleaseLogos({
    modalType,
    pdfHeadingLogoLeft,
    pdfHeadingLogoRight
  });
  const previewBlobUrlRef = useRef<null | string>(null);
  useEffect(() => {
    const updatePreview = async () => {
      const iframe = iframeRef.current;
      const contentElement = pdfContentRef.current;
      if (!contentElement || !iframe) return;
      const contentScrollHeight = contentElement.scrollHeight;
      const pdfDocument = new jsPDF({
        compress: true
      });
      pdfDocumentRef.current = pdfDocument;
      if (boldFontBinaryString !== null && regularFontBinaryString && !isFontLoading && isFormSubmitted) {
        const vfsRegularFontName = 'Noto-Sans-Regular.ttf';
        const vfsBoldFontName = 'Noto-Sans-Bold.ttf';
        pdfDocument.addFileToVFS(vfsRegularFontName, regularFontBinaryString);
        pdfDocument.addFileToVFS(vfsBoldFontName, boldFontBinaryString);
        pdfDocument.addFont(vfsRegularFontName, PDF_FONT_FAMILY.propertyValue, 'normal');
        pdfDocument.addFont(vfsBoldFontName, PDF_FONT_FAMILY.propertyValue, 'bold');
        pdfDocument.setFont(PDF_FONT_FAMILY.propertyValue);
      } else {
        pdfDocument.setFont('helvetica');
      }
      const pageWidth = pdfDocument.internal.pageSize.getWidth();
      const addHeaderImages = () => {
        if (modalType === PressReleasePdfType.MDRTFoundationGrant) {
          pdfDocument.addImage(pdfHeadingLogoLeft, '', 10, 5, leftLogoWidthRef.current, leftLogoHeightRef.current, 'left-logo');
          pdfDocument.addImage(pdfHeadingLogoRight, '', pageWidth - rightLogoWidthRef.current - 10, 5, rightLogoWidthRef.current, rightLogoHeightRef.current, 'right-logo');
        } else if (pdfHeaderImage) {
          pdfDocument.addImage(pdfHeaderImage, '', 0, 0, pageWidth, 35, 'header-image');
        }
      };
      const pageHeight = pdfDocument.internal.pageSize.getHeight();
      const topMargin = 41;
      const bottomMargin = 10;
      const contentHeightMm = pageHeight - topMargin - bottomMargin;
      const mmPerPx = pageWidth / JSPDF_WINDOW_WIDTH;
      const contentHeightPx = contentHeightMm / mmPerPx;
      const elementRect = contentElement.getBoundingClientRect();
      const linkData = Array.from(contentElement.querySelectorAll<HTMLAnchorElement>('a[href]')).map(anchor => {
        const rect = anchor.getBoundingClientRect();
        return {
          height: rect.height,
          href: encodeURI(anchor.href),
          width: rect.width,
          // eslint-disable-next-line id-length
          x: rect.left - elementRect.left,
          // eslint-disable-next-line id-length
          y: rect.top - elementRect.top
        };
      });
      const pageCutPointsPx: number[] = [];
      const blocks = Array.from(contentElement.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6'));
      let nextBoundary = contentHeightPx;
      while (nextBoundary < contentScrollHeight) {
        let bestCut = 0;
        for (const block of blocks) {
          const blockBottom = block.getBoundingClientRect().bottom - elementRect.top;
          if (blockBottom <= nextBoundary && blockBottom > bestCut) {
            bestCut = blockBottom;
          }
        }
        const cutPoint = bestCut > 0 ? bestCut : nextBoundary;
        pageCutPointsPx.push(cutPoint);
        nextBoundary = cutPoint + contentHeightPx;
      }
      setIsPDFGenerating(true);
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => resolve());
      });
      try {
        // eslint-disable-next-line import/no-extraneous-dependencies
        const {
          default: html2canvas
        } = await import('html2canvas');
        const fullCanvas = await html2canvas(contentElement, {
          height: contentScrollHeight,
          scale: PDF_SCALE,
          useCORS: true,
          width: JSPDF_WINDOW_WIDTH,
          windowWidth: JSPDF_WINDOW_WIDTH
        });
        const pageStartsPx = [0, ...pageCutPointsPx];
        const pageEndsPx = [...pageCutPointsPx, contentScrollHeight];
        for (const [pageIndex, startPx] of pageStartsPx.entries()) {
          const sliceHeightPx = pageEndsPx[pageIndex] - startPx;
          if (pageIndex > 0) {
            pdfDocument.addPage();
          }
          addHeaderImages();
          const sourceY = Math.round(startPx * PDF_SCALE);
          const sliceCanvasHeightPx = Math.min(Math.round(sliceHeightPx * PDF_SCALE), fullCanvas.height - sourceY);
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = fullCanvas.width;
          sliceCanvas.height = sliceCanvasHeightPx;
          const context = sliceCanvas.getContext('2d');
          if (context) {
            context.drawImage(fullCanvas, 0, sourceY, fullCanvas.width, sliceCanvasHeightPx, 0, 0, fullCanvas.width, sliceCanvasHeightPx);
          }
          pdfDocument.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 0, topMargin, pageWidth, sliceHeightPx * mmPerPx);
        }

        // eslint-disable-next-line id-length
        for (const {
          height,
          href,
          width,
          x,
          y
        } of linkData) {
          let pageIndex = pageStartsPx.length - 1;
          for (const [index, element] of pageEndsPx.entries()) {
            if (y < element) {
              pageIndex = index;
              break;
            }
          }
          const pdfX = x * mmPerPx;
          const pdfY = (y - pageStartsPx[pageIndex]) * mmPerPx + topMargin;
          pdfDocument.setPage(pageIndex + 1);
          pdfDocument.link(pdfX, pdfY, width * mmPerPx, height * mmPerPx, {
            url: href
          });
        }
        if (!iframeRef.current) return;
        if (previewBlobUrlRef.current) {
          URL.revokeObjectURL(previewBlobUrlRef.current);
        }
        const blob = pdfDocument.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        previewBlobUrlRef.current = blobUrl;
        iframe.src = blobUrl;
      } catch (error) {
        console.error(error);
      } finally {
        setIsPDFGenerating(false);
      }
    };
    updatePreview();
    return () => {
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boldFontBinaryString, htmlTemplate, isFontLoading, isFormSubmitted, modalType, pdfHeaderImage, regularFontBinaryString]);
  const handleSubmit = () => {
    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
      link_url: 'Continue',
      link_label: templateName,
      label: 'MemberKit-PressReleaseTemplate'
    });
    setIsFormSubmitted(true);
  };
  const commonSectionsProps = {
    containerClass: FORM_CONTAINER_CLASSNAME,
    setActiveField
  };
  return isPressReleaseModalType ? <ModalTrigger isOpen={pathname === HOMEPAGE_PATH && modalType !== null} onOpenChange={open => !open && handleModalClose()}>
      {() => <Dialog contentClassName={styles['dialog__content']} {...modalProps} className={classNames(styles['dialog'], {
      [styles['dialog--submitted']]: isFormSubmitted
    })} footer={<DialogActions onSecondaryButtonClick={isPreviewMode ? () => setIsPreviewMode(false) : isFormSubmitted ? () => setIsFormSubmitted(false) : handleModalClose} primaryButtonForm={isFormSubmitted ? 'press-release-pdf-download-form' : 'press-release-pdf-form'} primaryButtonLabel={isFormSubmitted ? cmsContent.generalModalContent.downloadButtonLabel : isPreviewMode ? '' : cmsContent.generalModalContent.continueButtonLabel} primaryButtonType={ButtonType.SUBMIT} secondaryButtonLabel={isFormSubmitted ? cmsContent.generalModalContent.backButtonLabel : cmsContent.generalModalContent.cancelButtonLabel} />} isLoading={isLoading} key={`step-${isFormSubmitted ? 'pdf-download' : 'form'}`}>
          <FormWrapper className={styles['dialog__preview-and-form-wrapper']} dataTestId="press-release-pdf-form" formMethods={formMethods} id="press-release-pdf-form" inert={isFormSubmitted} key="form" onSubmit={handleSubmit}>
            {!isHtmlTemplateLoading && !isFontLoading && htmlTemplate && <div className={styles['dialog__pdf-document-content-container']}>
                <PdfPreview activeField={activeField} htmlTemplate={htmlTemplate} locale={locale} pdfDocumentData={pdfDocumentData} />
              </div>}
            <div className={styles[`${FORM_CONTAINER_CLASSNAME}`]} ref={formContainerRef}>
              <Alert message={cmsContent.formContent.informationalMessage} variant={AlertVariant.Informational} />
              {modalType !== PressReleasePdfType.MDRTFoundationGrant && <PhotoSection cmsContent={{
            addPhotoLabel: cmsContent.formContent.addPhotoLabel,
            cancelLabel: cmsContent.generalModalContent.cancelButtonLabel,
            changePhotoLabel: cmsContent.formContent.changePhotoLabel,
            photoCropAriaLiveText: cmsContent.formContent.photoCropAriaLiveText,
            removePhotoLabel: cmsContent.formContent.removePhotoLabel,
            saveChangesLabel: cmsContent.formContent.saveChanges
          }} profilePictureSource={profilePictureSource} savedProfilePictureSource={savedProfilePictureSource} setSavedProfilePictureSource={setProfilePictureOverride} />}
              <AnnouncementDateSection cmsContent={{
            announcementDateDescription: cmsContent.formContent.announcementDateDescription,
            announcementDateHeading: cmsContent.formContent.announcementDateHeading,
            requiredLabel: cmsContent.generalModalContent.requiredLabel
          }} {...commonSectionsProps} />
              <PersonalInformationSection cmsContent={{
            city: cmsContent.formContent.city,
            designation: cmsContent.formContent.designation,
            firstName: cmsContent.formContent.firstName,
            lastName: cmsContent.formContent.lastName,
            latinOnlyMessage: cmsContent.generalModalContent.latinOnlyMessage,
            middleName: cmsContent.formContent.middleName,
            personalInformationSectionTitle: cmsContent.formContent.personalInformationSectionTitle,
            requiredLabel: cmsContent.generalModalContent.requiredLabel,
            stateProvince: cmsContent.formContent.stateProvince
          }} {...commonSectionsProps} modalType={modalType} />
              <PersonalAnecdoteSection cmsContent={{
            personalAnecdoteDescription: cmsContent.formContent.personalAnecdoteDescription,
            personalAnecdoteLabel: cmsContent.formContent.personalAnecdoteLabel
          }} {...commonSectionsProps} />
              <ContactInformationSection cmsContent={{
            areaCode: cmsContent.formContent.areaCode,
            contactInformationSectionTitle: cmsContent.formContent.contactInformationSectionTitle,
            email: cmsContent.formContent.email,
            invalidEmailValidationMessage: cmsContent.formContent.invalidEmailValidationMessage,
            isoCode: cmsContent.formContent.isoCode,
            phoneNumber: cmsContent.formContent.phoneNumber,
            requiredLabel: cmsContent.generalModalContent.requiredLabel
          }} {...commonSectionsProps} phoneIsoCodeOptions={phoneIsoCodeOptions} />
              <ProfessionalInformationSection cmsContent={{
            company: cmsContent.formContent.company,
            facebookLabel: cmsContent.formContent.facebookLabel,
            instagramLabel: cmsContent.formContent.instagramLabel,
            jobTitle: cmsContent.formContent.jobTitle,
            linkedInLabel: cmsContent.formContent.linkedInLabel,
            linkInputValidationLabel: cmsContent.generalModalContent.linkInputValidationLabel,
            personalCompany: cmsContent.formContent.personalCompany,
            professionalInformationSectionTitle: cmsContent.formContent.professionalInformationSectionTitle,
            requiredLabel: cmsContent.generalModalContent.requiredLabel,
            twitterXLabel: cmsContent.formContent.twitterXLabel
          }} {...commonSectionsProps} />
              {modalType === PressReleasePdfType.MDRTFoundationGrant && <DonationSection cmsContent={{
            charityNameInputLabel: cmsContent.formContent.charityNameInputLabel,
            donationReasonInputLabel: cmsContent.formContent.donationReasonInputLabel,
            donationSectionTitle: cmsContent.formContent.donationSectionTitle,
            grantAmountInputLabel: cmsContent.formContent.grantAmountInputLabel,
            requiredLabel: cmsContent.generalModalContent.requiredLabel
          }} {...commonSectionsProps} />}
            </div>
          </FormWrapper>
          {!isHtmlTemplateLoading && !isFontLoading && htmlTemplate && <div aria-hidden inert key="pdf-preview">
              <PdfPreview activeField={activeField} className={classNames(styles['dialog__printable-pdf-view'], {
          [styles['dialog__printable-pdf-view--printing']]: isFormSubmitted
        })} htmlTemplate={htmlTemplate} isPrintable locale={locale} pdfDocumentData={pdfDocumentData} ref={pdfContentRef} />
            </div>}
          {isFormSubmitted && <PdfDownloadModalContent cmsContent={{
        ...cmsContent.pdfDownloadModalContent,
        errorMessage: cmsContent.generalModalContent.errorMessage
      }} emailContent={{
        emailAddress: formMethods.getValues('email'),
        emailBody: selectedPdfType?.emailBody ?? '',
        emailSubject: selectedPdfType?.emailSubject ?? '',
        fileName: selectedPdfType?.fileName ?? 'Press_Release.pdf',
        fullName: customerProfile.fullName ?? '',
        pdfDocument: pdfDocumentRef.current
      }} handleModalClose={handleModalClose} isPreviewMode={isPreviewMode} key="pdf-download" ref={iframeRef} setIsPDFGenerating={setIsPDFGenerating} setIsPreviewMode={setIsPreviewMode} templateName={templateName} />}
        </Dialog>}
    </ModalTrigger> : null;
};